import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  Close as CloseIcon,
  Link as LinkIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import axiosInstance from "../utlis/axiosInstance";

interface DeviceUserConnectionsFormProps {
  id: string | null;
  open: boolean;
  onClose: () => void;
}

interface DropdownItem {
  label: string;
  value: string;
  email?: string;
}

interface ExistingConnection {
  id: number;
  user: number | null;
  device: number;
  group: any;
  geofence: any;
  notification: any;
  calendar: any;
  attribute: any;
  driver: any;
  managed_user: any;
  command: any;
  geofence_exit_alert: boolean;
  user_name: string;
  user_email?: string;
}

const DeviceUserConnectionsForm: React.FC<DeviceUserConnectionsFormProps> = ({
  id,
  open,
  onClose,
}) => {
  const [users, setUsers] = useState<DropdownItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [existingConnections, setExistingConnections] = useState<ExistingConnection[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Array<{ id: number; name: string; email?: string }>>([]);
  const [addingUserId, setAddingUserId] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  const showSuccessMessage = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage("");
    }, 3000);
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/gpstracker/userhandler/");
      if (response.data && response.data.responseData) {
        setUsers(
          response.data.responseData.map((item: any) => ({
            label: item.email ? `${item.name} (${item.email})` : item.name,
            value: item.id.toString(),
            email: item.email,
          }))
        );
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  // Fetch existing connections for the device
  const fetchExistingConnections = async () => {
    if (!id) return;
    try {
      const response = await axiosInstance.get(
        `/gpstracker/configurationhandler/?limit=1000&offset=0&device_id=${id}`
      );
      if (response.data && response.data.responseData && Array.isArray(response.data.responseData)) {
        // Filter connections to only show minimal ones (most fields null/false)
        const filteredConnections = response.data.responseData.filter((connection: any) => {
          return connection.user &&
            connection.device &&
            connection.id &&
            !connection.group &&
            !connection.geofence &&
            !connection.notification &&
            !connection.calendar &&
            !connection.attribute &&
            !connection.driver &&
            !connection.managed_user &&
            !connection.command &&
            connection.geofence_exit_alert === false;
        });

        setExistingConnections(filteredConnections);
        const selectedUsersList: Array<{ id: number; name: string; email?: string }> = [];
        response.data.responseData.forEach((connection: any) => {
          // Only show connections where most fields are null/false, keeping only id, device, and user
          const isMinimalConnection =
            connection.user &&
            connection.device &&
            connection.id &&
            !connection.group &&
            !connection.geofence &&
            !connection.notification &&
            !connection.calendar &&
            !connection.attribute &&
            !connection.driver &&
            !connection.managed_user &&
            !connection.command &&
            connection.geofence_exit_alert === false;

          if (isMinimalConnection) {
            // Try to find email from users list, or use user_email if available in connection
            const userFromList = users.find((u: DropdownItem) => u.value === connection.user?.toString());
            const email = userFromList?.email || connection.user_email || undefined;

            selectedUsersList.push({
              id: connection.user,
              name: connection.user_name,
              email: email,
            });
          }
        });
        setSelectedUsers(selectedUsersList);
      } else {
        setExistingConnections([]);
        setSelectedUsers([]);
      }
    } catch (error) {
      console.error("Error fetching existing connections:", error);
      setExistingConnections([]);
      setSelectedUsers([]);
    }
  };

  // Add user to device
  const addUser = async (userId: string) => {
    if (!id) return;
    setAddingUserId(userId);
    try {
      const requestBody = {
        user_id: parseInt(userId),
        device_id: parseInt(id),
        group_id: null,
        geofence_id: null,
        notification_id: null,
      };
      await axiosInstance.post("/gpstracker/configurationhandler/", requestBody);
      showSuccessMessage("User linked to device successfully");
      await fetchExistingConnections();
    } catch (error) {
      console.error("Error adding user to device:", error);
      // You can add error notification here if needed
    } finally {
      setAddingUserId(null);
    }
  };

  // Remove user from device
  const removeUser = async (userId: string) => {
    if (!id) return;
    setRemovingUserId(userId);
    try {
      // Find the connection id for this user-device link
      const connection = existingConnections.find(
        (conn) => conn.user?.toString() === userId
      );
      if (!connection) return;
      await axiosInstance.delete(`/gpstracker/configurationhandler/${connection.id}`);
      showSuccessMessage("User unlinked from device successfully");
      await fetchExistingConnections();
    } catch (error) {
      console.error("Error removing user from device:", error);
      // You can add error notification here if needed
    } finally {
      setRemovingUserId(null);
    }
  };

  useEffect(() => {
    if (open && id) {
      setIsLoading(true);
      fetchUsers().then(() => {
        fetchExistingConnections().finally(() => setIsLoading(false));
      });
    }
  }, [open, id]);

  // Update email information for selected users when users list is loaded
  useEffect(() => {
    if (users.length > 0 && selectedUsers.length > 0) {
      const updatedUsers = selectedUsers.map((selectedUser) => {
        const userFromList = users.find((u: DropdownItem) => u.value === selectedUser.id.toString());
        if (userFromList?.email && !selectedUser.email) {
          return {
            ...selectedUser,
            email: userFromList.email,
          };
        }
        return selectedUser;
      });

      // Only update if there are changes
      const hasChanges = updatedUsers.some((updated, index) =>
        updated.email !== selectedUsers[index]?.email
      );
      if (hasChanges) {
        setSelectedUsers(updatedUsers);
      }
    }
  }, [users]);

  if (!open || !id) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "white",
          border: "1px solid",
          borderColor: "#e2e8f0",
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <LinkIcon sx={{ mr: 1, color: "#1565C0" }} />
            <Typography variant="h5" fontWeight={600} color="#1565C0">
              Device-User Connections
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#1565C0" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            {successMessage}
          </Alert>
        )}
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress sx={{ color: "#1565C0" }} />
            <Typography variant="body1" sx={{ ml: 2, color: "#1565C0" }}>
              Loading connections...
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: "#1565C0", fontWeight: 600 }}>
                <PersonIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Linked Users
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {selectedUsers.length === 0 ? (
                  <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No users linked to this device.
                  </Typography>
                ) : (
                  selectedUsers.map((user) => (
                    <Chip
                      key={user.id}
                      label={user.email ? `${user.name} (${user.email})` : user.name}
                      onDelete={() => removeUser(user.id.toString())}
                      deleteIcon={<DeleteIcon />}
                      sx={{
                        backgroundColor: "#e3f2fd",
                        color: "#1565C0",
                        "& .MuiChip-deleteIcon": {
                          color: "#FF3D00",
                        },
                      }}
                    />
                  ))
                )}
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: "#1565C0", fontWeight: 600 }}>
                Add User
              </Typography>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select user to link..."
                    size="small"
                    disabled={isLoading || !!addingUserId}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {params.InputProps.startAdornment}
                          {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#e3f2fd",
                        "&:hover": { bgcolor: "#bbdefb" },
                        "&.Mui-focused": { bgcolor: "white" },
                      },
                    }}
                  />
                )}
                onChange={(event, newValue) => {
                  if (newValue && newValue.value) {
                    addUser(newValue.value);
                  }
                }}
                loading={isLoading}
                disabled={isLoading || !!addingUserId}
                clearOnBlur
                clearOnEscape
                selectOnFocus
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceUserConnectionsForm;
